import React, { useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { ExternalLink, FileText } from 'lucide-react';

const DriveFile = ({ file }) => {
    const user_settings = useSelector((state) => state.auth.user_settings);
    const isMinimalView = user_settings?.minimal_mode;
    const nameRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const handleFileClick = () => {
        window.open(file.web_view_link, '_blank');
    };

    useEffect(() => {
        const checkTruncation = () => {
            if (nameRef.current) {
                setIsTruncated(nameRef.current.scrollWidth > nameRef.current.clientWidth);
            }
        };
        checkTruncation();
        window.addEventListener('resize', checkTruncation);
        return () => window.removeEventListener('resize', checkTruncation);
    }, [file.name]);

    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        const kb = bytes / 1024;
        const mb = kb / 1024;
        return mb > 1 ? `${mb.toFixed(1)} MB` : `${kb.toFixed(0)} KB`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    return (
    <div data-name="drive-file-container" className="flex w-full min-w-0">
        <div 
            onClick={handleFileClick}
            className="folder-container w-full cursor-pointer bg-chart-1/10 border-chart-1/30 hover:bg-chart-1/20 flex"
        >
            {/* Content */}
            <div className="flex-1 min-w-0 flex flex-col items-start">
                <div className="flex items-center gap-1.5 w-full overflow-hidden">
                    {file.icon_link ? (
                        <img 
                            src={file.icon_link} 
                            alt=""
                            className="w-4 h-4 flex-shrink-0"
                        />
                    ) : (
                        <FileText className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                    )}
                    {/* Wrap text in a div with min-w-0 flex-1, just like FolderItem */}
                    <div className="min-w-0 flex-1">
                        {isTruncated ? (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span ref={nameRef} className="text-sm truncate block text-primary cursor-pointer">
                                        {file.name}
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent 
                                    side="top" 
                                    className="bg-white text-black border border-gray-300 shadow-md font-sans max-w-xs break-words"
                                >
                                    {file.name}
                                </TooltipContent>
                            </Tooltip>
                        ) : (
                            <span ref={nameRef} className="text-sm truncate block text-primary">
                                {file.name}
                            </span>
                        )}
                    </div>
                </div>
                {!isMinimalView && (
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground mt-0.5 pl-0.5">
                        <span>{formatFileSize(file.size)}</span>
                        {file.modified_time && (
                            <>
                                <span>•</span>
                                <span>{formatDate(file.modified_time)}</span>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Right side - External link icon */}
            <div className="flex items-center justify-center ml-1 flex-shrink-0 w-10">
                <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
            </div>
        </div>
    </div>
);
};

export default DriveFile;

